import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navbar } from "./Navbar/Navbar";
import { useNavigate } from "react-router-dom";
import { HouseIcon, PlayIcon } from "lucide-react";
import { fetchData } from "../utils/fetch";
import ButtonWithSound from "./ButtonWithSound/ButtonWithSound";

const FRUITS = ["🍎", "🍌", "🍇", "🍊", "🍓", "🍑"];
const DANGERS = ["🐍", "🐝"];
const POWER_UP = "⭐";
const ALL_ITEMS = [...FRUITS, ...DANGERS, POWER_UP];
const INITIAL_LIVES = 3;
const INITIAL_SPEED = 2;
const SPEED_MULTIPLIER = 1.5;
const SPEED_UP_DURATION = 5000;
const BASKET = "🧺";
const ITEM_SIZE = 40;

const FruitCatcher = () => {
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(INITIAL_LIVES);
  const [gameOver, setGameOver] = useState(false);
  const [fallingItems, setFallingItems] = useState([]);
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [isSpeedUp, setIsSpeedUp] = useState(false);
  const [highscore, setHighScore] = useState(() => {
    const saved = localStorage.getItem("highscore");
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [isPaused, setIsPaused] = useState(false);
  const [basketPosition, setBasketPosition] = useState({
    x: window.innerWidth / 2,
    y: window.innerHeight - 100,
  });
  const gameAreaRef = useRef(null);
  const animationFrameId = useRef(null);
  const lastItemAddTime = useRef(Date.now());
  const getRandom = () => Math.floor(Math.random() * ALL_ITEMS.length);
  const isSpeedUpRef = useRef(isSpeedUp);

  const navigate = useNavigate();

  const generateNewItem = (prevItems) => {
    let emojiIndx = getRandom();

    if (ALL_ITEMS[emojiIndx] === POWER_UP || isSpeedUp) {
      const indx = prevItems.findIndex((item) => item.emoji === POWER_UP);
      if (indx > -1 || isSpeedUp) {
        emojiIndx -= 1;
      }
    }

    return {
      id: Date.now(),
      emoji: ALL_ITEMS[emojiIndx],
      position: Math.random() * (window.innerWidth - ITEM_SIZE),
      top: -ITEM_SIZE,
    };
  };

  const addItem = () => {
    setFallingItems((prevItems) => [...prevItems, generateNewItem(prevItems)]);
    lastItemAddTime.current = Date.now();
  };

  const removeItem = useCallback((id) => {
    setFallingItems((prevItems) => prevItems.filter((item) => item.id !== id));
  }, []);

  const collectingSound = () => {
    try {
      const audio = new Audio("/click.mp3");
      audio.play();
    } catch (error) {
      console.error("Error playing collecting sound:", error);
    }
  };

  const missedsound = () => {
    try {
      const audio = new Audio("/miss.mp3");
      audio.play();
    } catch (error) {
      console.error("Error playing missed sound:", error);
    }
  };

  const powerUpSound = () => {
    try {
      const audio = new Audio("/power.mp3");
      audio.play();
    } catch (error) {
      console.error("Error playing power-up sound:", error);
    }
  };
  useEffect(() => {
    isSpeedUpRef.current = isSpeedUp;
  }, [isSpeedUp]);

  // For increasing speed with respect to time
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isSpeedUpRef.current) {
        setSpeed((prev) => prev + 0.2);
      }
    }, 20000);

    return () => clearInterval(interval);
  }, []);

  const missed = () => {
    try {
      const audio = new Audio("/heartlose.mp3");
      audio.play();
    } catch (error) {
      console.error("Error playing heart lose sound:", error);
    }
  };
  const handleItemCollision = useCallback(
    (item) => {
      if (FRUITS.includes(item.emoji)) {
        setScore((prevScore) => {
          const newScore = prevScore + (isSpeedUp ? 50 : 10);
          collectingSound();
          if (newScore > highscore) {
            setHighScore(newScore);
          }
          return newScore;
        });
      } else if (item.emoji === POWER_UP) {
        powerUpSound();
        setIsSpeedUp(true);
        setSpeed((prevSpeed) => prevSpeed * SPEED_MULTIPLIER);
        setTimeout(() => {
          setIsSpeedUp(false);
          setSpeed(INITIAL_SPEED);
        }, SPEED_UP_DURATION);
      } else if (DANGERS.includes(item.emoji)) {
        console.log("collided with", item.emoji);
        missedsound();
        setGameOver(true);
      }
      removeItem(item.id);
    },
    [removeItem, isSpeedUp, highscore]
  );

  const handleMissedItem = useCallback(
    (item) => {
      if (FRUITS.includes(item.emoji)) {
        console.log("Missed", item.emoji);
        setLives((prevLives) => {
          missed();
          const newLives = prevLives > 0 ? prevLives - 1 : 0;
          if (newLives === 0) {
            setGameOver(true);
          }
          return newLives;
        });
      }
      removeItem(item.id);
    },
    [removeItem]
  );

  const gameLoop = useCallback(() => {
    if (isPaused) return;

    const currentTime = Date.now();
    if (currentTime - lastItemAddTime.current >= 500) {
      addItem();
    }

    setFallingItems((prevItems) =>
      prevItems
        .map((item) => {
          const newTop = item?.top + speed;
          const isColliding =
            newTop + ITEM_SIZE > basketPosition.y &&
            newTop < basketPosition.y + ITEM_SIZE &&
            item.position + ITEM_SIZE > basketPosition.x &&
            item.position < basketPosition.x + ITEM_SIZE;

          if (isColliding) {
            handleItemCollision(item);
            return null;
          }

          if (newTop > window.innerHeight) {
            handleMissedItem(item);
            return null;
          }

          return { ...item, top: newTop };
        })
        .filter(Boolean)
    );

    if (!gameOver && !isPaused) {
      animationFrameId.current = requestAnimationFrame(gameLoop);
    }
  }, [
    gameOver,
    handleItemCollision,
    handleMissedItem,
    speed,
    basketPosition,
    isPaused,
  ]);

  useEffect(() => {
    if (gameOver || isPaused) return;

    animationFrameId.current = requestAnimationFrame(gameLoop);

    return () => {
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [gameOver, gameLoop, isPaused]);

  const restartGame = () => {
    setScore(0);
    setLives(INITIAL_LIVES);
    setGameOver(false);
    setFallingItems([]);
    setSpeed(INITIAL_SPEED);
    setIsSpeedUp(false);
    setIsPaused(false);
    setBasketPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight - 100,
    });
    lastItemAddTime.current = Date.now();
    animationFrameId.current = requestAnimationFrame(gameLoop);
  };

  const togglePause = () => {
    setIsPaused((prevPaused) => !prevPaused);
    if (isPaused) {
      animationFrameId.current = requestAnimationFrame(gameLoop);
    } else {
      cancelAnimationFrame(animationFrameId.current);
    }
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!gameOver) {
        const rect = gameAreaRef.current.getBoundingClientRect();
        setBasketPosition({
          x: e.clientX - rect.left - ITEM_SIZE / 2,
          y: e.clientY - rect.top - ITEM_SIZE / 2,
        });
      }
    },
    [gameOver]
  );

  useEffect(() => {
    if (gameOver && highscore > localStorage.getItem("highscore")) {
      localStorage.setItem("highscore", highscore);
      (() => {
        fetchData("put", "/highscore", {
          id: localStorage.getItem("id"),
          highscore,
        });
      })();
    }
  }, [gameOver]);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const rect = gameAreaRef.current.getBoundingClientRect();
    setBasketPosition({
      x: touch.clientX - rect.left - ITEM_SIZE / 2,
      y: touch.clientY - rect.top - ITEM_SIZE / 2,
    });
  }, []);

  return (
    <div
      ref={gameAreaRef}
      className="relative w-full h-screen overflow-hidden"
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      style={{
        backgroundImage: "url('/green5.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Navbar
        score={score}
        lives={lives}
        highScore={highscore}
        pause={togglePause}
      />
      {fallingItems.map((item) => (
        <div
          key={item.id}
          className="absolute text-4xl transition-transform"
          style={{ left: `${item.position}px`, top: `${item.top}px` }}
        >
          {item.emoji}
        </div>
      ))}
      <div
        className="absolute text-4xl z-20 pointer-events-auto touch-none"
        style={{
          left: `${basketPosition.x}px`,
          top: `${basketPosition.y}px`,
          cursor: "none",
        }}
      >
        {BASKET}
      </div>
      {gameOver && (
        <div className="absolute z-50 inset-0 text-white text-center bg-black bg-opacity-30 flex items-center justify-center">
          <div className="p-4 bg-green-700 rounded-lg shadow-lg h-fit w-[70%] sm:w-1/3 opacity-80">
            <h2 className="text-3xl font-bold mb-4 ">Game Over!</h2>
            <p className="text-xl mb-4">Your score: {score}</p>
            <p className="text-xl mb-4">Your highest score: {highscore}</p>
            <ButtonWithSound
              className="px-4 py-2 rounded-2xl bg-yellow-500 flex items-center gap-x-10  w-full font-semibold text-xl text-white  hover:bg-yellow-600 transition-colors"
              onClick={restartGame}
            >
              <PlayIcon /> <h2>Play Again</h2>
            </ButtonWithSound>
            <ButtonWithSound
              className="px-4 rounded-2xl flex items-center gap-x-10  z-50 mt-3 py-2 bg-blue-500 w-full font-semibold text-xl text-white hover:bg-blue-600 transition-colors"
              onClick={() => navigate("/")}
            >
              <HouseIcon /> <h2>Home</h2>
            </ButtonWithSound>
          </div>
        </div>
      )}
    </div>
  );
};

export default FruitCatcher;
